import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import {
  Button,
  Card,
  CardBody,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import Datetime from "react-datetime";
import moment from "moment";
import NumberFormat from "react-number-format";
import classNames from "classnames";
import { updateIncome } from "../../helpers/api/finance";
import { findAllCurrencies } from "../../helpers/api/currency";
import { fetchAccountingAreas } from "../../helpers/api/accountingAreas";
import {
  convertToUsd,
  formatAmountRounded,
  getCurrencyName,
} from "../../helpers/currencyConversion";
import useExchangeRateForDate from "../../helpers/hooks/useExchangeRateForDate";
import { CurrencyContext } from "../../context/Context";
import { Toast } from "../UI/toast/Toast";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import FileUploadArea from "../common/FileUploadArea";
import InputWithLeftSelect from "../common/CurrencyAmountInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { fetchAllPages } from "../../helpers/api/pagination";

const EditIncomeModal = ({ isOpen, toggle, income, condominiumId, onSuccess }) => {
  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const activeExchangeRate = Number(curr?.value ?? 0);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [montoOriginal, setMontoOriginal] = useState("");
  const [selectedCurrencyId, setSelectedCurrencyId] = useState("");
  const [incomeDate, setIncomeDate] = useState(moment().format("YYYY-MM-DD HH:mm:ss"));
  const [destination, setDestination] = useState("PLANILLA");
  const [accountingAreaId, setAccountingAreaId] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingVoucher, setExistingVoucher] = useState(null);
  const [accountingAreas, setAccountingAreas] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [hasLoadedCurrencies, setHasLoadedCurrencies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (income) {
      setName(income.name || "");
      setDescription(income.description || "");
      setMontoOriginal(income.amount || "");
      setDestination(income.destination || "PLANILLA");
      setAccountingAreaId(
        income.accounting_area_id || income.accounting_area?.id || ""
      );
      if (income.created_at || income.income_date) {
        setIncomeDate(moment(income.created_at || income.income_date).format("YYYY-MM-DD HH:mm:ss"));
      }
      setVoucher(null);
      setImagePreview(null);
      setExistingVoucher(income.voucher || income.voucher_url || null);
    }
  }, [income]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setVoucher(selectedFile);
      setImagePreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleFileRemove = () => {
    setVoucher(null);
    setImagePreview(null);
    setExistingVoucher(null);
  };

  const currentPreview = imagePreview || existingVoucher;
  const currentFileName = voucher?.name || (existingVoucher ? (existingVoucher.split("/").pop() || "Comprobante de pago") : "");

  const loadCurrencies = useCallback(async () => {
    if (!condominiumId) return;
    setHasLoadedCurrencies(false);
    try {
      const response = await fetchAllPages((page, pageSize) => findAllCurrencies(page, pageSize, {
        condominium_id: condominiumId,
      }));
      setAvailableCurrencies(response?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setHasLoadedCurrencies(true);
    }
  }, [condominiumId]);

  const loadAccountingAreas = useCallback(async () => {
    if (!condominiumId) return;
    try {
      const response = await fetchAccountingAreas({ condominiumId });
      setAccountingAreas(response?.data || []);
    } catch (error) {
      console.error(error);
    }
  }, [condominiumId]);

  useEffect(() => {
    if (isOpen) {
      loadCurrencies();
      loadAccountingAreas();
    }
  }, [isOpen, loadCurrencies, loadAccountingAreas]);

  const currencyCatalog = useMemo(() => {
    const normalized = (availableCurrencies || []).map((item) => ({
      ...item,
      id: String(item.id),
      iso: (item?.iso || "").toUpperCase(),
      isFallbackUsd: false,
    }));

    const hasUsd = normalized.some((item) => item.iso === baseCurrency.iso);
    if (!hasUsd) {
      normalized.push({
        id: baseCurrency.iso,
        iso: baseCurrency.iso,
        name: getCurrencyName(baseCurrency.iso),
        isFallbackUsd: true,
      });
    }

    return normalized;
  }, [availableCurrencies, baseCurrency.iso]);

  const preferredDefaultCurrency = useMemo(() => {
    const fallbackByBase = currencyCatalog.find(
      (item) => (item?.iso || "").toUpperCase() === baseCurrency.iso
    );

    return fallbackByBase || currencyCatalog[0] || null;
  }, [baseCurrency.iso, currencyCatalog]);

  useEffect(() => {
    if (!hasLoadedCurrencies || !preferredDefaultCurrency || selectedCurrencyId) {
      return;
    }
    setSelectedCurrencyId(String(preferredDefaultCurrency.id));
  }, [hasLoadedCurrencies, preferredDefaultCurrency, selectedCurrencyId]);

  const selectedCurrencyData = useMemo(() => {
    const currentValue = String(selectedCurrencyId || "");
    return (currencyCatalog || []).find(
      (item) =>
        String(item.id) === currentValue ||
        item.iso === currentValue.toUpperCase()
    );
  }, [currencyCatalog, selectedCurrencyId]);

  const isSelectedCurrencyUsd =
    (selectedCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

  const parsedIncomeDate = useMemo(() => {
    if (!incomeDate) return null;
    if (moment.isMoment(incomeDate) && incomeDate.isValid()) return incomeDate;

    const parsed = moment(incomeDate, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD", "DD/MM/YYYY"]);
    return parsed.isValid() ? parsed : null;
  }, [incomeDate]);

  const {
    isRateLoading,
    appliedExchangeRate,
    rateResolution,
  } = useExchangeRateForDate({
    currencyId: selectedCurrencyData?.id,
    isUsdCurrency: isSelectedCurrencyUsd || Boolean(selectedCurrencyData?.isFallbackUsd),
    date: parsedIncomeDate,
    activeExchangeRate,
    condominiumId,
  });

  const rateHelpText = useMemo(() => {
    if (isSelectedCurrencyUsd) return "";
    if (isRateLoading) return "Consultando tasa...";
    if (!appliedExchangeRate) return "";

    const effectiveDateKey = rateResolution?.usedDateKey || rateResolution?.selectedDateKey;
    const effectiveRateDate = effectiveDateKey
      ? moment(effectiveDateKey)
      : (parsedIncomeDate?.isValid() ? parsedIncomeDate : moment());

    const dateLabel = effectiveRateDate.format("DD/MM/YYYY");
    const rateLabel = formatAmountRounded(appliedExchangeRate, 2);
    return `Tasa ${dateLabel}: ${rateLabel}`;
  }, [appliedExchangeRate, isRateLoading, isSelectedCurrencyUsd, parsedIncomeDate, rateResolution]);

  const calculatedUsdAmount = useMemo(() => {
    return convertToUsd(
      montoOriginal,
      isSelectedCurrencyUsd,
      appliedExchangeRate
    );
  }, [montoOriginal, isSelectedCurrencyUsd, appliedExchangeRate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!income) return;

    if (!isSelectedCurrencyUsd && (!appliedExchangeRate || appliedExchangeRate <= 0 || rateResolution?.isFallback)) {
      Toast.fire({
        type: "error",
        title: "No hay una tasa del dia disponible para convertir esta moneda a USD.",
      });
      return;
    }

    const normalizedUsdAmount = convertToUsd(
      montoOriginal,
      isSelectedCurrencyUsd,
      appliedExchangeRate
    );

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("description", description || "");
      formData.append("amount", normalizedUsdAmount);
      formData.append("destination", destination);
      formData.append("condominium_id", condominiumId || income.condominium_id);
      formData.append("is_common_type", income.is_common_type ? 1 : 0);
      formData.append("reference", income.reference || "N/A");

      if (parsedIncomeDate) {
        formData.append("income_date", parsedIncomeDate.format("YYYY-MM-DD HH:mm:ss"));
      }

      if (accountingAreaId) {
        formData.append("accounting_area_id", accountingAreaId);
      }

      if (voucher) {
        formData.append("voucher", voucher);
      }

      const { code } = await updateIncome(income.id, formData);
      if (code === "H0004" || code === "H0000" || code === "OK" || code === 200) {
        Toast.fire({
          type: "success",
          title: "Ingreso actualizado exitosamente.",
        });
        toggle();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>Editar Ingreso</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label for="income_name">Nombre del ingreso *</Label>
            <Input
              type="text"
              id="income_name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label for="income_description">Descripción</Label>
            <Input
              type="textarea"
              id="income_description"
              rows="2"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label for="edit_income_date">Fecha del ingreso</Label>
            <Datetime
              id="edit_income_date"
              value={parsedIncomeDate || incomeDate}
              onChange={(d) => {
                let formattedDate = "";
                if (moment.isMoment(d) && d.isValid()) {
                  formattedDate = d.format("YYYY-MM-DD HH:mm:ss");
                } else if (typeof d === "string") {
                  const parsed = moment(d, ["DD/MM/YYYY", "YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss"]);
                  formattedDate = parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : d;
                } else if (d) {
                  const parsed = moment(d);
                  if (parsed.isValid()) formattedDate = parsed.format("YYYY-MM-DD HH:mm:ss");
                }
                setIncomeDate(formattedDate);
              }}
              dateFormat="DD/MM/YYYY"
              timeFormat={false}
            />
          </FormGroup>

          <FormGroup>
            <Label for="edit_income_amount">Monto *</Label>
            <InputWithLeftSelect
              selectValue={String(selectedCurrencyId || selectedCurrencyData?.id || "")}
              onSelectChange={(e) => setSelectedCurrencyId(String(e.target.value))}
              selectOptions={(currencyCatalog || []).map((item) => ({
                value: String(item.id),
                label: `${item.iso} (${item.name})`,
              }))}
              helpText={rateHelpText}
              selectAriaLabel="Moneda original"
              selectMinWidth="120px"
            >
              <NumberFormat
                customInput={Input}
                thousandSeparator
                prefix={
                  selectedCurrencyData?.iso
                    ? `${selectedCurrencyData.iso} `
                    : `${baseCurrency.symbol} `
                }
                fixedDecimalScale
                decimalScale={2}
                onValueChange={(vals) => setMontoOriginal(vals.value)}
                value={montoOriginal}
                placeholder="0.00"
                className="font-weight-bold border-left-0"
              />
            </InputWithLeftSelect>
            {isRateLoading && (
              <small className="text-muted d-block mt-1">
                <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                Consultando tasa para la fecha...
              </small>
            )}
          </FormGroup>

          {Boolean(montoOriginal) && !isSelectedCurrencyUsd && (
            <Card className="border-warning bg-soft-warning mb-3">
              <CardBody className="py-3">
                <div className="d-flex align-items-start">
                  <div>
                    {Boolean(calculatedUsdAmount) && (
                      <div className="text-700 fs--1 font-weight-semi-bold mb-1">
                        Monto equivalente a registrar: ${" "}
                        <NumberFormat
                          displayType="text"
                          value={formatAmountRounded(calculatedUsdAmount, 2)}
                          thousandSeparator
                          decimalScale={2}
                          fixedDecimalScale
                        />{" "}
                        USD
                      </div>
                    )}
                    <div className="text-700 fs--1">
                      El monto que se guardará en el sistema será el equivalente en {getCurrencyName(baseCurrency.iso)} ({baseCurrency.symbol}).
                      El valor mostrado arriba es solo la referencia original ingresada por el usuario.
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          <FormGroup>
            <Label for="income_destination">Destino de los fondos *</Label>
            <Input
              type="select"
              id="income_destination"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            >
              <option value="PLANILLA">Planilla de liquidación</option>
              <option value="CASH">Caja / Banco</option>
              <option value="RESERVE_FUND">Fondo de reserva</option>
            </Input>
          </FormGroup>

          {accountingAreas.length > 0 && (
            <FormGroup>
              <Label for="income_accounting_area">Área Contable</Label>
              <Input
                type="select"
                id="income_accounting_area"
                value={accountingAreaId}
                onChange={(e) => setAccountingAreaId(e.target.value)}
              >
                <option value="">Seleccione área contable...</option>
                {accountingAreas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
          )}

          <FormGroup>
            <FileUploadArea
              label="Comprobante de pago (opcional)"
              onChange={handleFileChange}
              preview={currentPreview}
              fileName={currentFileName}
              onRemove={handleFileRemove}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button color="primary" type="submit" disabled={isSubmitting || isRateLoading}>
            {isSubmitting ? <Spinner size="sm" /> : "Guardar cambios"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EditIncomeModal;
